import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import TagManager from 'react-gtm-module';
import * as Sentry from '@sentry/browser';

import { Route, Switch, Redirect } from 'react-router';
import { ConnectedRouter } from 'connected-react-router'
import { history } from './store/store';
import { CONFIG } from './config';

import PasswordReset from './containers/PasswordReset';
import ForgottenPassword from './containers/ForgottenPassword';
import Login from './containers/Login';
import FootfallInsights from './containers/FootfallInsights';
import TravellingPastInsights from './containers/TravellingPastInsights';
import Home from './containers/Home';
import LoadingClientData from './containers/LoadingClientData';
import Account from './containers/Account';
import ReportHome from './containers/ReportHome';
import HomeInsights from './containers/HomeInsights';
import WorkInsights from './containers/WorkInsights';
import BrandAffinityInsights from './containers/BrandAffinityInsights'
import MobileDevicesInsights from './containers/MobileDevicesInsights'
import ErrorBoundary from './containers/ErrorBoundary';
import MainTain from './containers/MainTain';
import SelectAccount from './containers/SelectAccount';
import AudienceBuilder from './containers/AudienceBuilder';
import AudienceBuilderSelectType from './containers/AudienceBuilderSelectType';
import CampaignSummary from './containers/CampaignSummary';

import { isThisBrowserAvailable, isThisScreenSizeAvailable } from './utilities/browser_device'

// Google Tag Manager init
const tagManagerArgs = {
  gtmId: CONFIG.GTM_ID,
};
TagManager.initialize(tagManagerArgs);

// Sentry init
const environment = () => {
  if (window.location.href.indexOf('localhost') > -1){
    return 'Local';
  }else if(window.location.href.indexOf('-dev') > -1){
    return 'Development';
  }else if(window.location.href.indexOf('-staging') > -1){
    return 'Staging';
  }else if (window.location.href.indexOf('-demo') > -1){
    return 'Demo';
  }else{
    return 'Production';
  }
};

environment() !== 'Local' && (
  Sentry.init({
    dsn: CONFIG.SENTRY_DSN,
    release: `intelligence-platform-app@${process.env.REACT_APP_VERSION}`,
    environment: environment(),
    integrations: [new Sentry.Integrations.Breadcrumbs({
        console: false
    })]
  })
)



const reportFactory = reportId => {
  switch (reportId) {
    case 'footfall':
      return <ErrorBoundary><FootfallInsights reportId={reportId} /></ErrorBoundary>;
    case 'travelPast':
      return <ErrorBoundary><TravellingPastInsights reportId={reportId} /></ErrorBoundary>;
    case 'home':
      return <ErrorBoundary><HomeInsights reportId={reportId} /></ErrorBoundary>;
    case 'work':
      return <ErrorBoundary><WorkInsights reportId={reportId} /></ErrorBoundary>;
    case 'brandAffinity':
      return <ErrorBoundary><BrandAffinityInsights reportId={reportId} /></ErrorBoundary>;
    case 'mobileDevices':
      return <ErrorBoundary><MobileDevicesInsights reportId={reportId} /></ErrorBoundary>;
    default:
      return <h1>Report not found</h1>;
  }
};

const mapStateToProps = ({ auth, client, reports, account }) => ({
  isAuthenticated: auth.isAuthenticated,
  clientDataLoaded: client.clientDataLoaded,
  showHomePage: client.showHomePage,
  availableReports: reports.availableReports,
  serverStatus: account.serverStatus,
  client: client,
  reportDataLoaded: reports.reportDataLoaded,
  clientError: reports.clientError,
});

const AppRouter = props => {
  //pass addtional info to sentry
  Sentry.configureScope(function(scope) {
    scope.setUser({
      id: props.client.id,
      name: props.client.name,
      accountType: props.client.accountType,
      country: props.client.country
    });
  });

 return (
  <ConnectedRouter history={history}>
    <Switch>
      {isThisBrowserAvailable() === false && (
        <Route render={() => <MainTain errorType="browser_not_support"/>} />
      )}
      {isThisScreenSizeAvailable() === true && (
        <Route render={() => <MainTain errorType="devices_not_support"/>} />
      )}
      {props.serverStatus.status !== true && (
        <Route render={() => <MainTain errorType="server_error"/>} />
      )}
      <Route
        path="/password-reset/:token"
        render={({ match }) => <PasswordReset token={match.params.token} />}
      />
      <Route exact path="/select-account" render={() => <SelectAccount />} />
      <Route path="/forgotten-password" render={() => <ForgottenPassword />} />
      {props.isAuthenticated !== true && <Route render={() => <Login />} />}
      {props.clientDataLoaded !== true && (
        <Route render={() => <LoadingClientData />} />
      )}

      {props.clientError === true && (
        <Route render={() => <MainTain errorType="client_data_error"/>} />
      )}

      <Route exact path="/" render={() => props.showHomePage ? <Home /> : <ReportHome />} />
      <Route exact path="/account" render={() => <Account />} />
      <Route exact path="/insight-landing" render={() => <ReportHome />} />
      <Route exact path="/maintain" render={() => <MainTain errorType="downtime_maintain"/>} />
      {props.availableReports.map(report => (
        <Route
          path={report.path}
          key={report.reportId}
          render={() => reportFactory(report.reportId)}
        />
      ))}
      <Route exact path="/audience-builder" render={() => <AudienceBuilder />} />
      <Route exact path="/audience-builder-select-type" render={() => <AudienceBuilderSelectType />} />
      <Route exact path="/campaign-summary" render={() => <CampaignSummary />} />
      <Route exact path="/login" render={() => <Login />} />
      <Route exact path="/loading" render={() => <LoadingClientData />} />
      <Route
        render={() => (
          <Redirect to="/" />
        )}
      />
    </Switch>
  </ConnectedRouter>
)};

AppRouter.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  clientDataLoaded: PropTypes.bool.isRequired,
  availableReports: PropTypes.arrayOf(PropTypes.object).isRequired,
  showHomePage: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(AppRouter);
